import React, { useState, useEffect, useReducer } from 'react';
import { apiErrorToast } from '../../toast/ToastHandler';

import { reducer, ActionKind, iState, Action } from './reducer';

type iEditState<T> = {
  isModalOpen: boolean;
  version: number;
  target?: T;
  keyword?: string;
  perPage: number;
  currentPage: number;
  sort?: string;
  filter?: string;
  delTargetId?: string;
};
export type iUseListCrudHookProps = {
  //  eslint-disable-next-line
  createFn?: (content: any) => any;
  //  eslint-disable-next-line
  updateFn?: (id: string, content: any) => any;
  deleteFn?: (id: string) => void;
  //  eslint-disable-next-line
  getFn: (config?: { [key: string]: string }) => any;
  keywordColumns?: string;
  perPage?: number;
  loadMoreApplied?: boolean;
  paginationApplied?: boolean;
  sort?: string;
  filter?: string;
  notRenderWithoutFilter?: boolean;
};
const useListCrudHook = <T extends { id: string }>({
  createFn,
  deleteFn,
  updateFn,
  getFn,
  keywordColumns,
  perPage = 10,
  paginationApplied = true,
  loadMoreApplied = false,
  sort,
  filter,
  notRenderWithoutFilter = false,
}: iUseListCrudHookProps) => {
  const initialState: iState<T> = {
    data: [],
    isLoading: false,
    isConfirming: false,
  };
  const initialEditState: iEditState<T> = {
    isModalOpen: false,
    version: 0,
    perPage,
    currentPage: 1,
    sort,
    filter,
  };
  const [state, dispatch] = useReducer<React.Reducer<iState<T>, Action<T>>>(reducer, initialState);
  const [edit, setEdit] = useState(initialEditState);

  useEffect(() => {
    let isCancelled = false;
    //  interface for configs
    const getConfig = () => {
      let likeConfig;
      let paginationConfig;
      let sortConfig;
      let filterConfig;
      if (edit.sort) {
        sortConfig = { sort: edit.sort };
      }
      if (paginationApplied || loadMoreApplied) {
        paginationConfig = {
          perPage: edit.perPage.toString(),
          currentPage: edit.currentPage.toString(),
        };
      }
      if (
        typeof edit.keyword !== 'undefined' &&
        typeof keywordColumns !== 'undefined' &&
        keywordColumns.trim() !== ''
      ) {
        const columns = keywordColumns.split(',');
        likeConfig = {
          like: columns?.map((column: string) => `${column}:${edit.keyword}`).join(','),
        };
      }
      if (edit.filter) {
        filterConfig = { filter: edit.filter };
      }
      return {
        ...paginationConfig,
        ...likeConfig,
        ...sortConfig,
        ...filterConfig,
      };
    };
    const fetchData = async () => {
      //  not render with filter to save bandwidth
      if (edit.filter === undefined && notRenderWithoutFilter) {
        return;
      }
      dispatch({ type: ActionKind.Loading, payload: {} });
      try {
        //  eslint-disable-next-line
        const fetchResult: any = await getFn(getConfig());
        if (isCancelled) return;
        //  load more is to append resolved result to original list
        //  general pagination is to replace original list by resolved result
        dispatch({
          type: loadMoreApplied ? ActionKind.LoadMore : ActionKind.Loaded,
          payload:
            paginationApplied || loadMoreApplied
              ? {
                  data: fetchResult.data,
                  from: fetchResult.from,
                  to: fetchResult.to,
                  total: fetchResult.total,
                  currentPage: fetchResult.currentPage,
                  perPage: fetchResult.perPage,
                }
              : { data: fetchResult },
        });
      } catch (error) {
        if (isCancelled) return;
        apiErrorToast(error);
        dispatch({
          type: ActionKind.Reset,
          payload: {},
        });
      }
    };

    // console.log({
    //   edit,
    //   paginationApplied,
    //   loadMoreApplied,
    //   getFn,
    //   keywordColumns,
    //   notRenderWithoutFilter,
    // });
    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [
    edit.version,
    edit.keyword,
    edit.perPage,
    edit.currentPage,
    edit.sort,
    edit.filter,
    paginationApplied,
    loadMoreApplied,
    getFn,
    keywordColumns,
    notRenderWithoutFilter,
  ]);

  // eslint-disable-next-line
  const onSubmit = async (data: any) => {
    dispatch({ type: ActionKind.Confirming, payload: {} });
    // console.log(edit.target);
    try {
      if (typeof updateFn !== 'function') return;
      if (typeof createFn !== 'function') return;

      const result = edit.target ? await updateFn(edit.target.id, data) : await createFn(data);
      // eslint-disable-next-line
      const eagerLoadResult: any = await getFn({ filter: `id:${result.id}` });
      dispatch({
        type: edit.target ? ActionKind.Update : ActionKind.Add,
        payload: { item: eagerLoadResult[0] },
      });
      setEdit({
        ...edit,
        target: undefined,
        isModalOpen: false,
      });
    } catch (error) {
      dispatch({ type: ActionKind.Confirmed, payload: {} });
      apiErrorToast(error);
    }
  };
  const onDelete = async (deleteTargetId: string) => {
    try {
      if (typeof deleteFn !== 'function') return;
      dispatch({ type: ActionKind.Confirming, payload: {} });
      await deleteFn(deleteTargetId);
      dispatch({
        type: ActionKind.Delete,
        payload: { targetId: deleteTargetId },
      });
      setEdit({
        ...edit,
        delTargetId: undefined,
        isModalOpen: false,
      });
    } catch (error) {
      apiErrorToast(error);
      dispatch({ type: ActionKind.Confirmed, payload: {} });
    }
  };
  const onOpenAddModal = () =>
    setEdit({
      ...edit,
      isModalOpen: true,
      target: undefined,
      delTargetId: undefined,
    });

  const onOpenEditModal = (targetId: string) => {
    // console.log(state.data);
    const target = state.data.find((u: T) => u.id === targetId);
    setEdit({ ...edit, isModalOpen: true, target, delTargetId: undefined });
  };
  const onOpenDeleteModal = (targetId: string) => {
    const target = state.data.find((u: T) => u.id === targetId);
    setEdit({
      ...edit,
      isModalOpen: true,
      delTargetId: targetId,
      target,
    });
  };
  const onCloseModal = () =>
    setEdit({
      ...edit,
      isModalOpen: false,
      target: undefined,
      delTargetId: undefined,
    });

  const onSearch = (keyword: string) =>
    setEdit({
      ...edit,
      keyword,
    });
  const onFilter = (value: string) => {
    setEdit({
      ...edit,
      filter: value,
    });
  };
  const onSetCurrentPage = (page: number) => setEdit({ ...edit, currentPage: page });
  const onSetSort = (sortStr: string) => setEdit({ ...edit, currentPage: 1, sort: sortStr });
  const onRefresh = () => {
    setEdit({ ...edit, version: edit.version + 1 });
  };
  const onRefreshListToFirstPage = () => {
    setEdit({ ...edit, currentPage: 1, version: edit.version + 1 });
  };
  return {
    state,
    edit,
    getEdit: () => edit,
    onSubmit,
    onCloseModal,
    onDelete,
    onOpenEditModal,
    onOpenAddModal,
    onSearch,
    onSetCurrentPage,
    onSetSort,
    onFilter,
    onOpenDeleteModal,
    onRefresh,
    onRefreshListToFirstPage,
  };
};

export default useListCrudHook;
